// d3-org-chart wrapper.
//
// Mounts the d3 chart into a ref'd div on first render, then calls
// chart.data(...).render() whenever the `data` prop changes. The chart
// instance lives in a ref so React state changes don't tear it down.
//
// Card content ported from worker.js (lines 485-526) with one change:
// role banner colors map to our CSS tokens instead of literal hex, so
// the chart respects dark/light theme.

import { useEffect, useRef } from 'react';
import { OrgChart as D3OrgChart } from 'd3-org-chart';

function getBadgeClass(util) {
  if (util >= 80) return 'util-good';
  if (util >= 50) return 'util-warn';
  return 'util-poor';
}

function bannerColorFor(role) {
  // The CSS variables are theme-aware; falling back to hex for the
  // role-band so it stays legible against the card body regardless of
  // theme. (d3-org-chart renders inline styles into SVG, so we can't
  // use Tailwind classes for the banner — literal CSS only.)
  if (role === 'Company')   return '#030712';
  if (role === 'Executive') return '#1F2937';
  if (role === 'Manager')   return '#1F2937';
  if (role === 'Team Lead') return '#4B5563';
  return '#9CA3AF';
}

export default function OrgChart({ data }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !data?.nodes) return;
    // Re-parent visible roots to a single empty-string root so the
    // d3-org-chart library can render them. (Library requires every
    // entry to have a parentId, with exactly one root using ''.)
    const nodes = data.nodes.map(n => ({ ...n }));
    // If multiple visible roots exist, synthesize a parent to hold them
    // all so they render side-by-side. With one root, the empty string
    // is the natural root marker.
    const roots = nodes.filter(n => !n.parentId);
    if (roots.length > 1) {
      nodes.push({
        id: '__synthetic_root__',
        parentId: '',
        name: 'Visible',
        role: 'Company',
        position: '',
        utilization: 0,
        teamUtilization: 0,
        trackedHours: 0,
        hubstaffHours: 0,
        hasTeam: true,
      });
      for (const r of roots) r.parentId = '__synthetic_root__';
    }

    if (!chartRef.current) {
      chartRef.current = new D3OrgChart()
        .container(containerRef.current)
        .data(nodes)
        .nodeWidth(() => 280)
        .nodeHeight(() => 145)
        .childrenMargin(() => 40)
        .compactMarginBetween(() => 25)
        .compactMarginPair(() => 25)
        .compact(true)
        .linkUpdate(function () {
          // eslint-disable-next-line no-undef
          window.d3?.select?.(this)?.attr?.('stroke', 'var(--border)').attr?.('stroke-width', 1.5);
        })
        .nodeContent(renderNodeContent)
        .render();
    } else {
      chartRef.current.data(nodes).render();
    }
  }, [data]);

  // Tear down on unmount.
  useEffect(() => () => {
    try { chartRef.current?.clear?.(); } catch {}
    chartRef.current = null;
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full h-[calc(100vh-280px)] min-h-[480px] rounded-lg border border-border bg-bg overflow-hidden"
    />
  );
}

function renderNodeContent(d) {
  const node = d.data;
  const isManager = !!node.hasTeam;
  const badgeClass = getBadgeClass(node.utilization || 0);
  const teamBadgeClass = isManager ? getBadgeClass(node.teamUtilization || 0) : '';
  const bgTitle = bannerColorFor(node.role);

  // Synthetic root (multi-root parent) renders as an empty band so it
  // doesn't clutter the chart.
  if (node.id === '__synthetic_root__') {
    return `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: var(--muted); font-size: 11px;">Visible team</div>`;
  }

  const utilHtml = isManager
    ? `<div style="display: flex; gap: 6px; width: 100%;">
         <span class="util-badge ${badgeClass}" style="flex: 1;">Self: ${node.utilization}%</span>
         <span class="util-badge ${teamBadgeClass}" style="flex: 1;">Team: ${node.teamUtilization}%</span>
       </div>`
    : `<span class="util-badge ${badgeClass}" style="width: 100%;">${node.utilization}% Utilized</span>`;

  const positionText = node.position ? String(node.position).replace(/"/g, '&quot;') : '';
  const nameText = node.name ? String(node.name).replace(/"/g, '&quot;') : '';

  return `
    <div style="padding-top: 10px; height: 100%; box-sizing: border-box;">
      <div style="background-color: var(--panel); border: 1px solid var(--border); border-radius: 8px; height: 100%; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
        <div style="background-color: ${bgTitle}; color: white; padding: 8px 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${positionText}">
          ${positionText || node.role || 'Employee'}
        </div>
        <div style="padding: 12px; flex: 1; display: flex; flex-direction: column; justify-content: space-between;">
          <div style="font-size: 14px; font-weight: 700; color: var(--text); margin-bottom: 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${nameText}">
            ${nameText}
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 12px; color: var(--muted); margin-bottom: 10px;">
            <div>Tracked: <strong style="color: var(--text);">${node.trackedHours}h</strong></div>
            <div>Hubstaff: <strong style="color: var(--text);">${node.hubstaffHours}h</strong></div>
          </div>
          <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
            ${utilHtml}
          </div>
        </div>
      </div>
    </div>
  `;
}
